import { GrowthBook, GrowthBookProvider as GBProvider, useGrowthBook } from "@growthbook/growthbook-react";
import {
  buildExperimentViewedParams,
  DeviceType,
  FeatureFlags,
  useApp,
  useEvent,
  useKindeFeatureFlags,
  useUserInfo,
  useUserSettings,
} from "@next-platform/client-common-core";
import { applicationRuntime } from "@next-platform/client-common-core/infra/application/capabilities";
import basicAppConfig from "@next-platform/client-common-core/infra/constants/basicAppConfig";
import appLogger from "@next-platform/client-common-core/logger/appLogger";
import { createContext, MutableRefObject, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { useTitleWatcher } from "@/hooks";
import { useStableStringArray } from "@/hooks/useStableStringArray";
import { getBrowserDevice, getCookie, getURLAttributes, getUtmAttributes } from "@/utils";

const COUNTRY = getCookie("country");

const EMPTY_STRING_ARRAY: string[] = [];
type GrowthBookInitOptions = NonNullable<Parameters<GrowthBook["init"]>[0]>;
type GrowthBookInitPayload = NonNullable<GrowthBookInitOptions["payload"]>;

interface IGrowthBookAttributesInput {
  applicationId: string | null;
  currentTitle: string;
  guestUserId: string | null;
  legacyKindeFeatureFlags: string[];
  location: ReturnType<typeof useLocation>;
  onlyUserId: string | null;
  permissions: string[];
  role: string | null;
  roles: string[];
  subscription: ReturnType<typeof useUserInfo>["subscription"];
  subscriptionTier: string | null;
}

function createGrowthBookAttributes({
  applicationId,
  currentTitle,
  guestUserId,
  legacyKindeFeatureFlags,
  location,
  onlyUserId,
  permissions,
  role,
  roles,
  subscription,
  subscriptionTier,
}: IGrowthBookAttributesInput) {
  return {
    applicationId,
    id: onlyUserId,
    guestUserId,
    userCountry: COUNTRY,
    userLanguage: navigator.language,
    subscriptionRole: role,
    subscriptionTier,
    subscriptionPlatform: subscription?.platform,
    subscriptionStatus: subscription?.status,
    subscriptionBillingCycle: subscription?.billing_cycle,
    subscriptionStartDate: subscription?.start_date,
    pageTitle: currentTitle,
    ...getURLAttributes(location),
    ...getBrowserDevice(navigator.userAgent),
    devicePlatform: DeviceType.WEB,
    ...getUtmAttributes(new URL(window.location.href)),
    roles,
    permissions,
    legacyKindeFeatureFlags,
    isLoaded: true,
  };
}

interface IFeatureFlagProviderProps {
  children: ReactNode;
}

interface IUseRuntimeFeatureFlagsInput {
  growthbookConfig: GrowthBookInitPayload | null;
  stateGb: GrowthBook;
  stateGrowthbookRevision: number;
  stateIsGbReady: boolean;
}

interface IUseInitializeGrowthBookInput {
  bumpRevision: () => void;
  growthbookConfig: GrowthBookInitPayload | null;
  markGbReadyIfLoaded: () => void;
  refGbInitialized: MutableRefObject<boolean>;
  stateGb: GrowthBook;
}

const featureFlagLogger = appLogger.withTag("FeatureFlagProvider");

export const FeatureFlagContext = createContext<GrowthBook | null>(null);

function GrowthBookCapabilityInvalidator() {
  useGrowthBook();

  const refHasRenderedOnce = useRef(false);

  useEffect(() => {
    if (!refHasRenderedOnce.current) {
      refHasRenderedOnce.current = true;
      return;
    }

    applicationRuntime.invalidateFeatureFlags();
  });

  return null;
}

function useInitializeGrowthBook({ bumpRevision, growthbookConfig, markGbReadyIfLoaded, refGbInitialized, stateGb }: IUseInitializeGrowthBookInput) {
  useEffect(() => {
    if (growthbookConfig) {
      const initGrowthbook = async () => {
        try {
          await stateGb.init({ payload: growthbookConfig, streaming: false });
        } catch (error) {
          featureFlagLogger.error("Failed to initialize GrowthBook", error);
        } finally {
          refGbInitialized.current = true;
          markGbReadyIfLoaded();
          bumpRevision();
        }
      };
      void initGrowthbook();
    }
  }, [stateGb, growthbookConfig, bumpRevision, markGbReadyIfLoaded, refGbInitialized]);
}

function useRuntimeFeatureFlags({ growthbookConfig, stateGb, stateGrowthbookRevision, stateIsGbReady }: IUseRuntimeFeatureFlagsInput) {
  const featureFlagContext = useMemo(() => {
    return {
      getValue: <T,>(flagKey: string, defaultValue: T): T => {
        return stateGb.getFeatureValue(flagKey, defaultValue) as T;
      },
      isOn: (flagKey: string) => stateGb.isOn(flagKey),
    };
  }, [stateGb, stateGrowthbookRevision]);

  const featureFlagsForRuntime = stateIsGbReady || !growthbookConfig ? featureFlagContext : null;

  useEffect(() => {
    applicationRuntime.setFeatureFlags({
      featureFlags: featureFlagsForRuntime,
      subscribe: (listener) => stateGb.subscribe(listener),
    });
  }, [stateGb, featureFlagsForRuntime, stateGrowthbookRevision]);

  useEffect(() => {
    return () => {
      applicationRuntime.setFeatureFlags({ featureFlags: null });
    };
  }, []);
}

export function FeatureFlagProvider({ children }: IFeatureFlagProviderProps) {
  const { onlyUserId, guestUserId, role, subscriptionTier, subscription, roles, permissions, isAuthLoaded, isAuthenticated } = useUserInfo();
  const { areUserSettingsLoaded } = useUserSettings();
  const { hasAccessToV2 } = useKindeFeatureFlags();
  const { appSettings } = useApp();
  const applicationId = basicAppConfig.applicationId;
  const currentTitle = useTitleWatcher();
  const { sendEvent } = useEvent();

  const refSendEvent = useRef(sendEvent);
  const refOnlyUserId = useRef(onlyUserId);
  const refGuestUserId = useRef(guestUserId);

  // Keep refs up to date each render so the trackingCallback always reads current user context
  refSendEvent.current = sendEvent;
  refOnlyUserId.current = onlyUserId;
  refGuestUserId.current = guestUserId;

  const [stateGb] = useState(() => {
    return new GrowthBook({
      enableDevMode: import.meta.env.VITE_GROWTHBOOK_DEV_MODE === "true",
      clientKey: import.meta.env.VITE_GROWTHBOOK_CLIENT_KEY,
      decryptionKey: import.meta.env.VITE_GROWTHBOOK_DECRYPTION_KEY,
      trackingCallback: (experiment, result) => {
        const eventParams = buildExperimentViewedParams({
          experimentId: experiment.key,
          variationId: result.key,
          userId: refOnlyUserId.current,
          guestUserId: refGuestUserId.current,
        });
        void refSendEvent.current(eventParams);
      },
    });
  });

  const [stateGrowthbookRevision, setStateGrowthbookRevision] = useState(0);
  const bumpRevision = useCallback(() => setStateGrowthbookRevision((revision) => revision + 1), []);

  const refGbInitialized = useRef(false);
  const refGbAttributesApplied = useRef(false);
  const [stateIsGbReady, setStateIsGbReady] = useState(false);
  const markGbReadyIfLoaded = useCallback(() => {
    if (refGbInitialized.current && refGbAttributesApplied.current) {
      setStateIsGbReady(true);
    }
  }, []);

  const location = useLocation();
  const stableRoles = useStableStringArray(roles);
  const stablePermissions = useStableStringArray(permissions);
  const legacyKindeFeatureFlags = useMemo(() => {
    return hasAccessToV2 ? [FeatureFlags.V2] : EMPTY_STRING_ARRAY;
  }, [hasAccessToV2]);
  const growthbookConfig = useMemo<GrowthBookInitPayload | null>(() => {
    return (appSettings?.growthbookConfig as GrowthBookInitPayload | undefined) ?? null;
  }, [appSettings?.growthbookConfig]);

  const refAppliedAttributesSignature = useRef<string | null>(null);

  useInitializeGrowthBook({
    bumpRevision,
    growthbookConfig,
    markGbReadyIfLoaded,
    refGbInitialized,
    stateGb,
  });

  useEffect(() => {
    if (!isAuthLoaded || !growthbookConfig) {
      return;
    }

    if (isAuthenticated && !areUserSettingsLoaded) {
      return;
    }

    const attributes = createGrowthBookAttributes({
      applicationId,
      currentTitle,
      guestUserId,
      legacyKindeFeatureFlags,
      location,
      onlyUserId,
      permissions: stablePermissions,
      role,
      roles: stableRoles,
      subscription,
      subscriptionTier,
    });

    const attributesSignature = JSON.stringify(attributes);

    void stateGb.setAttributes(attributes).finally(() => {
      refGbAttributesApplied.current = true;
      markGbReadyIfLoaded();
      if (refAppliedAttributesSignature.current !== attributesSignature) {
        refAppliedAttributesSignature.current = attributesSignature;
        bumpRevision();
      }
    });
  }, [
    stateGb,
    growthbookConfig,
    applicationId,
    isAuthLoaded,
    isAuthenticated,
    areUserSettingsLoaded,
    onlyUserId,
    guestUserId,
    role,
    subscriptionTier,
    stablePermissions,
    stableRoles,
    subscription,
    location,
    currentTitle,
    legacyKindeFeatureFlags,
    bumpRevision,
    markGbReadyIfLoaded,
  ]);

  useRuntimeFeatureFlags({
    growthbookConfig,
    stateGb,
    stateGrowthbookRevision,
    stateIsGbReady,
  });

  return (
    <FeatureFlagContext.Provider value={stateGb}>
      <GBProvider growthbook={stateGb}>
        <GrowthBookCapabilityInvalidator />
        {children}
      </GBProvider>
    </FeatureFlagContext.Provider>
  );
}
